import React, { useEffect } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTable, { Column, Data } from "@Components/Table";
import AddPlusIcon from "@src/Constants/svg/AddPlusIcon";
import { useAppDispatch, useAppSelector } from "@hooks";
import { RootState } from "@Store";
import {
  organizationsListRequest,
  setModalCreateOrganization,
  setModalCreateOrganizationType,
} from "@Store/Organizations/actions";
import {
  selectOrganizationsGeneralData,
  selectOrganizationsList,
} from "@Store/Organizations/selectors";
import { IOrganization } from "@Store/Organizations/types";
import CreateOrganizationModal from "@Pages/System/CreateOrganizationModal";
import "./styles.scss";

interface OrganizationsPageProps {
  onOrgSelect: (orgId: string) => void;
}

const OrganizationsPage: React.FC<OrganizationsPageProps> = ({
  onOrgSelect,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { isLoading, organizationsList } = useAppSelector(
    (state: RootState) => ({
      isLoading: selectOrganizationsGeneralData(state).isLoading,
      organizationsList: selectOrganizationsList(state),
    })
  );

  useEffect(() => {
    dispatch(organizationsListRequest());
  }, [dispatch]);

  const handleCreate = () => {
    dispatch(setModalCreateOrganizationType("create"));
    dispatch(setModalCreateOrganization());
  };

  const handleEnter = (orgId: string) => {
    onOrgSelect(orgId);
    history.push("/corporate");
  };

  const columns: Column[] = [
    { field: "organization", headerName: t("organization.columns.name") },
    { field: "appId", headerName: t("organization.columns.appId") },
    {
      field: "matrixHomeserver",
      headerName: t("organization.columns.matrixHomeServer"),
    },
    { field: "actions", headerName: t("organization.columns.actions") },
  ];

  const data: Data[] = organizationsList.map(
    (organization: IOrganization, index: number) => ({
      id: index + 1,
      orgId: organization.orgId,
      organization: (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {organization.orgLogoUrl && (
            <img src={organization.orgLogoUrl} alt="" height={28} />
          )}
          <span>{organization.orgName}</span>
        </div>
      ),
      matrixHomeserver: organization.matrixHomeserver,
      appId: organization.appId,
      actions: (
        <Button
          variant="contained"
          color="warning"
          style={{ height: "25px", borderRadius: "4px" }}
          onClick={(event) => {
            event.stopPropagation();
            handleEnter(organization.orgId);
          }}
        >
          {t("organization.buttons.enter")}
        </Button>
      ),
    })
  );

  return (
    <Box className="root system-organizations-page">
      <div className="clients-organisation-header">
        <h2>{t("organization.title")}</h2>
        <Button
          variant="contained"
          color="primary"
          style={{ borderRadius: "4px" }}
          onClick={handleCreate}
        >
          <AddPlusIcon />
          <span
            style={{ marginLeft: "10px", color: "#000", fontWeight: "600" }}
          >
            {t("organization.buttons.addOrganization")}
          </span>
        </Button>
      </div>

      {isLoading && organizationsList.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 8,
          }}
        >
          <CircularProgress color="warning" />
        </Box>
      ) : (
        <CustomTable
          columns={columns}
          data={data}
          rowsPerPage={10}
          selectable={false}
        />
      )}

      <CreateOrganizationModal />
    </Box>
  );
};

export default OrganizationsPage;
